<template>
  <VSpinner v-if="!product" class="offset-top-16 offset-bottom-17" />
  <article v-else>
    <div id="PRODUCT_ADMINISTRATION_SUMMARY" class="container-fixed">
      <VProgress v-if="flags.progressBarEnabled" class="offset-top-2 offset-bottom-2" :steps="steps" active="substore.progress_summary" />

      <section v-if="product.texts.productName && product.texts.providerName">
        <div class="row">
          <div class="col-l-6">
            <h3 class="h1" v-html="product.texts.productName"></h3>
            <p>
              <strong>
                {{ $t('substore.provider') }}:
                <span v-html="product.texts.providerName"></span>
              </strong>
            </p>
          </div>
        </div>
        <hr />
      </section>

      <section v-if="product.texts.descriptionAdministrationSummary">
        <p v-html="product.texts.descriptionAdministrationSummary"></p>
      </section>

      <div v-if="product.texts.linkInfoTextAdministrationSummary">
        <br />
        <section>
          <span class="pull left" v-html="product.texts.linkInfoTextAdministrationSummary"></span>
          <div class="btn-sectioned pull-right">
            <button type="button" class="btn btn-default" @click="onChange">
              {{ $t('substore.change') }}
            </button>
          </div>
        </section>
        <br />
        <hr />
      </div>
    </div>

    <div class="container-fixed">
      <VForm :text-lists="product.textLists" v-bind="flags" @submit="btnNext">
        <section v-if="product.texts.linkDescriptionAdministrationSummary">
          <p v-html="product.texts.linkDescriptionAdministrationSummary"></p>
        </section>

        <div class="row">
          <section class="offset-top-2 pull-right offset-right-1 button-bar">
            <button type="button" class="btn btn-clean" @click="btnAbort">
              {{ $t('substore.abort') }}
            </button>
            <button type="submit" class="offset-left-1 btn btn-default btn-brand">
              {{ $t('substore.order_btn') }}
            </button>
          </section>
        </div>
      </VForm>
    </div>

    <div class="container-fixed offset-top-6">
      <VFootnotes :footnotes="footnotes" />
    </div>
  </article>
</template>

<script>
import { mapGetters } from 'vuex';
import AdministrationProxy from '@/proxies/SubstoreAdministrationProxy';
import AdministrationActionProxy from '@/proxies/SubstoreOrderAdministrationActionProxy';
import Footnotes from '@/utils/Footnotes';
import TitleMixin from '@/mixins/title';
import OrderStepsMixin from '@/mixins/administrationSteps';
import VSpinner from '@/components/Spinner';
import Transformer from '@/transformers/ProductAdministrationSummaryTransformer';
import {
  HOME,
  PRODUCT_ADMINISTRATION_CONFIRMATION,
  PRODUCT_ADMINISTRATION_CONFIRMATION_IN_PROGRESS,
  PRODUCT_ADMINISTRATION_SELECTION,
  PRODUCT_CATALOGUE,
  STOCK,
} from '@route-types';

export default {
  name: 'ProductAdministrationSummary',

  components: {
    VSpinner,
    VProgress: () => import('@/components/Progress'),
    VFootnotes: () => import('@/components/UI/Footnotes'),
    VForm: () => import('./Form'),
  },

  mixins: [TitleMixin, OrderStepsMixin],

  props: {
    subscriptionTemplateId: {
      type: String,
      required: true,
    },

    uniqueSubscriptionId: {
      type: String,
      required: true,
    },

    requestedParameterValue: {
      type: String,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      product: null,
      footnotes: null,
      flags: {
        progressBarEnabled: null,
        currentParameterValue: null,
      },
    };
  },

  computed: {
    ...mapGetters('system', ['hasAccessCatalogue', 'hasAccessStockOverview']),
  },

  async created() {
    if (!this.requestedParameterValue) {
      this.$router.replace({
        name: PRODUCT_ADMINISTRATION_SELECTION,
        params: {
          subscriptionTemplateId: this.subscriptionTemplateId,
          uniqueSubscriptionId: this.uniqueSubscriptionId,
        },
      });
      return;
    }
    const response = await new AdministrationProxy().orderAdministrationSummaryDialogData(
      this.subscriptionTemplateId,
      this.uniqueSubscriptionId,
      this.requestedParameterValue
    );

    Object.keys(this.flags).forEach(key => {
      this.flags[key] = response.data[key];
    });

    this.footnotes = new Footnotes();
    this.product = Transformer.fetch(response.data.product, this.footnotes);

    this.$webtrekk.viewPageWithPayload('administrationzusammenfassung', {
      page_type: 'basket',
      shop_product_sku: this.subscriptionTemplateId,
      shop_product_name: this.product.texts?.productName,
    });
  },

  methods: {
    btnAbort() {
      if (this.hasAccessStockOverview) {
        this.$router.push({ name: STOCK });
      } else if (this.hasAccessCatalogue) {
        this.$router.push({ name: PRODUCT_CATALOGUE });
      } else {
        this.$router.push({ name: HOME });
      }
    },

    async btnNext() {
      const params = {
        subscriptionTemplateId: this.subscriptionTemplateId,
        uniqueSubscriptionId: this.uniqueSubscriptionId,
        requestedParameterValue: this.requestedParameterValue,
      };
      const response = await new AdministrationActionProxy().create(params);
      let positiveErrorCode;
      if (response.head.fault !== null) {
        if (response.head.fault.isPositiveError) {
          positiveErrorCode = response.head.fault.args.reasonInfo;
        } else {
          throw new Error(response.head.fault);
        }
      }
      if (positiveErrorCode === '103') {
        this.$router.push({ name: PRODUCT_ADMINISTRATION_CONFIRMATION_IN_PROGRESS });
      } else {
        this.$router.push({
          name: PRODUCT_ADMINISTRATION_CONFIRMATION,
          params: {
            subscriptionTemplateId: this.subscriptionTemplateId,
            uniqueSubscriptionId: this.uniqueSubscriptionId,
          },
        });
      }
    },

    onChange() {
      this.$router.push({
        name: PRODUCT_ADMINISTRATION_SELECTION,
        params: {
          fixedLineNumber: this.requestedParameterValue,
          subscriptionTemplateId: this.subscriptionTemplateId,
        },
      });
    },
  },
};
</script>
